import React, { useState } from 'react';
import { bool, func, object, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { IconArrowHead } from '../../../components';

import css from './ShowOnlyFilters.module.css';

/**
 * ShowOnlyFilters
 *
 * Renders a collapsible "Show Only" section at the bottom of the filter sidebar
 * with a "Sold Out Items" checkbox. Visually matches the FilterPlain component
 * pattern used by the other sidebar filters.
 *
 * This is intentionally NOT config-driven like the other filters, because
 * "showSoldOut" is a UI-only URL param that is extracted before the Sharetribe
 * API call rather than being passed as a pub_* extended data filter. See:
 *   - SearchPage.duck.js  → stockFilters()
 *   - ProfilePage.duck.js → queryUserListings()
 *
 * Follows the same import pattern as FilterComponent — lives in the SearchPage
 * directory and is imported by other pages (e.g. ProfilePage) as needed.
 */
const ShowOnlyFilters = ({ urlQueryParams, onToggleSoldOut, className }) => {
  const [isOpen, setIsOpen] = useState(true);
  const isChecked = urlQueryParams?.showSoldOut === true || urlQueryParams?.showSoldOut === 'true';

  return (
    <div className={classNames(css.root, className)}>
      {/* Collapsible header — matches FilterPlain's labelButton pattern */}
      <div className={css.filterHeader}>
        <button
          className={css.labelButton}
          type="button"
          onClick={() => setIsOpen(prev => !prev)}
        >
          <span className={css.labelButtonContent}>
            <span className={css.label}>
              <FormattedMessage id="ShowOnlyFilters.heading" />
              {isChecked && (
                <span className={css.labelSelected}>
                  {' · 1'}
                </span>
              )}
            </span>
            <span className={css.chevronWrapper}>
              <IconArrowHead
                direction={isOpen ? 'up' : 'down'}
                size="small"
                className={css.chevronIcon}
              />
            </span>
          </span>
        </button>
      </div>

      {/* Checkbox options — visible when section is open */}
      {isOpen && (
        <div className={css.options}>
          <label className={css.checkboxLabel} htmlFor="showSoldOut">
            <input
              id="showSoldOut"
              type="checkbox"
              className={css.checkboxInput}
              checked={isChecked}
              onChange={() => onToggleSoldOut(!isChecked)}
            />
            {/* Custom checkbox visual — matches FieldCheckbox SVG style */}
            <span className={css.checkboxWrapper} aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                <g fill="none" fillRule="evenodd">
                  <rect
                    className={isChecked ? css.checked : css.unchecked}
                    x="0" y="0" width="16" height="16" rx="4"
                  />
                  <rect
                    className={css.box}
                    x="0.5" y="0.5" width="15" height="15" rx="3.5"
                    strokeWidth="1" fill="none"
                  />
                  {isChecked && (
                    <path
                      d="M4.5 8L7 10.5L11.5 5.5"
                      stroke="var(--colorWhite)"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  )}
                </g>
              </svg>
            </span>
            <span className={css.checkboxText}>
              <FormattedMessage id="ShowOnlyFilters.soldOutItems" />
            </span>
          </label>

          {/* Clear link — shown when filter is active, matches FilterPlain.clearButton */}
          {isChecked && (
            <button
              className={css.clearButton}
              type="button"
              onClick={() => onToggleSoldOut(false)}
            >
              <FormattedMessage id="ShowOnlyFilters.clear" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

ShowOnlyFilters.defaultProps = {
  className: null,
  urlQueryParams: {},
};

ShowOnlyFilters.propTypes = {
  className: string,
  urlQueryParams: object,
  onToggleSoldOut: func.isRequired,
};

export default ShowOnlyFilters;
